/**
 * NamespacePicker - custom field do Scaffolder.
 *
 * Pre-popula o campo de namespace com o padrao IBICT (<prefixo>-<grupo>),
 * derivado do ultimo segmento do `parameters.owner` preenchido pelo
 * GitLabGroupPicker do step anterior. O usuario pode editar o valor; uma
 * vez tocado, o auto-fill nao sobrescreve mais.
 *
 * O prefixo pode ser configurado no template via:
 *
 * ui:options:
 *   prefix: mysql
 *
 * Sem configuracao explicita, o padrao continua sendo "pgsql".
 */

import { useEffect, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const DEFAULT_PREFIX = 'pgsql';

const normalizePrefix = (prefix: unknown): string => {
  if (typeof prefix !== 'string') return DEFAULT_PREFIX;
  const normalized = prefix.trim().replace(/-+$/, '');
  return normalized || DEFAULT_PREFIX;
};

const deriveDefault = (
  owner: string | undefined,
  prefix = DEFAULT_PREFIX,
): string => {
  if (!owner) return '';
  const last = owner.split('/').pop() ?? '';
  return last ? `${prefix}-${last}` : '';
};

const NamespacePicker = (props: FieldExtensionComponentProps<string>) => {
  const {
    onChange,
    formData,
    rawErrors,
    required,
    schema,
    formContext,
    uiSchema,
  } = props;

  const owner: string | undefined =
    (formContext as any)?.formData?.owner ?? undefined;
  const prefix = normalizePrefix((uiSchema as any)?.['ui:options']?.prefix);

  const [edited, setEdited] = useState(() => {
    const initial = (formData ?? '').trim();
    return initial !== '' && initial !== deriveDefault(owner, prefix);
  });

  useEffect(() => {
    if (edited) return;
    const next = deriveDefault(owner, prefix);
    if (next && next !== formData) {
      onChange(next);
    }
    // Nao adicionar onChange como dep; Stepper recria a cada render
    // e isso geraria loop. owner, prefix e edited sao os triggers reais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, prefix, edited]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={Boolean(rawErrors?.length)}
      fullWidth
    >
      <TextField
        label={(schema.title as string) ?? 'Namespace'}
        value={formData ?? ''}
        onChange={e => {
          setEdited(true);
          onChange(e.target.value);
        }}
        helperText={
          (schema.description as string) ??
          `Pre-populado com ${prefix}-<grupo>; pode ser editado.`
        }
        variant="outlined"
        required={required}
        InputLabelProps={{ shrink: Boolean(formData) }}
      />
    </FormControl>
  );
};

export const NamespacePickerField = createFormField({
  name: 'NamespacePicker',
  component: NamespacePicker,
});

export const namespacePickerField = FormFieldBlueprint.make({
  name: 'namespace-picker',
  params: {
    field: async () => NamespacePickerField,
  },
});
